"use client";

import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "@/styles/CustomDatePicker.css";
import { cn } from "@/lib/utils";

registerLocale("ja", ja);

export interface CustomDatePickerProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
  showTimeSelect?: boolean;
  timeFormat?: string;
  className?: string;
  error?: string;
  required?: boolean;
  clearable?: boolean;
  inline?: boolean;
  showYearDropdown?: boolean;
  showMonthDropdown?: boolean;
  excludeDates?: Date[];
  includeDates?: Date[];
  filterDate?: (date: Date) => boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  leftIcon?: JSX.Element;
  showRightIcon?: boolean;
  onCalendarClose?: () => void;
  onCalendarOpen?: () => void;
}

interface CustomHeaderProps {
  date: Date;
  changeYear: (year: number) => void;
  changeMonth: (month: number) => void;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  onPickerStateChange?: (isPickerOpen: boolean) => void;
  minDate?: Date;
}

/**
 * CustomHeader
 *
 * A custom header for the calendar — manages month/year navigation,
 * swipe gestures (mobile), wheel scrolling, and dropdown-based selection
 * for months and years with minDate restrictions.
 */
const CustomHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  onPickerStateChange,
  minDate,
}: CustomHeaderProps) => {
  const [showMonthPicker, setShowMonthPicker] = React.useState(false);
  const [showYearPicker, setShowYearPicker] = React.useState(false);
  const currentYear = new Date().getFullYear();

  // Notify parent about picker state changes
  React.useEffect(() => {
    const isPickerOpen = showMonthPicker || showYearPicker;
    onPickerStateChange?.(isPickerOpen);
  }, [showMonthPicker, showYearPicker, onPickerStateChange]);

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i,
    label: format(new Date(2000, i), "M'月"),
    fullLabel: format(new Date(2000, i), "M'月"),
  }));

  // Generate years range (current year - 10 to current year + 10)
  const years = Array.from({ length: 51 }, (_, i) => currentYear - 10 + i);

  const handleMonthSelect = (month: number) => {
    // Only allow selection if month/year combination is not before minDate
    if (minDate) {
      const selectedDate = new Date(date.getFullYear(), month, 1);
      const minDateCompare = new Date(
        minDate.getFullYear(),
        minDate.getMonth(),
        1,
      );
      if (selectedDate < minDateCompare) {
        return; // Don't allow selection
      }
    }
    changeMonth(month);
    setShowMonthPicker(false);
  };

  const handleYearSelect = (year: number) => {
    // Only allow selection if year is not before minDate year
    if (minDate && year < minDate.getFullYear()) {
      return; // Don't allow selection
    }
    changeYear(year);
    setShowYearPicker(false);
  };

  // Handle wheel scroll for month navigation
  const handleWheel = React.useCallback(
    (e: Event) => {
      const wheelEvent = e as WheelEvent;
      const target = wheelEvent.target as HTMLElement;

      // Allow scrolling in year picker dropdown content area
      if (
        showYearPicker &&
        (target.closest(".year-picker-content") ||
          target.closest(".year-picker-dropdown"))
      ) {
        return; // Let the default scroll behavior work
      }

      // Don't handle wheel events if month picker is open (no scroll needed for month picker)
      if (showMonthPicker) {
        return;
      }

      wheelEvent.preventDefault();
      if (wheelEvent.deltaY > 0) {
        // Scroll down - next month
        if (!nextMonthButtonDisabled) {
          increaseMonth();
        }
      } else if (wheelEvent.deltaY < 0) {
        // Scroll up - previous month
        if (!prevMonthButtonDisabled) {
          decreaseMonth();
        }
      }
    },
    [
      increaseMonth,
      decreaseMonth,
      nextMonthButtonDisabled,
      prevMonthButtonDisabled,
      showMonthPicker,
      showYearPicker,
    ],
  );

  // Handle touch gestures for mobile swipe navigation
  const [touchStart, setTouchStart] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  const handleTouchStart = React.useCallback(
    (e: Event) => {
      const touchEvent = e as TouchEvent;
      const target = touchEvent.target as HTMLElement;

      // Don't handle touch if inside year picker content (allow scrolling)
      if (
        showYearPicker &&
        (target.closest(".year-picker-content") ||
          target.closest(".year-picker-dropdown"))
      ) {
        return;
      }

      // Don't handle touch if month picker is open
      if (showMonthPicker) {
        return;
      }

      const touch = touchEvent.touches[0];
      setTouchStart({ x: touch.clientX, y: touch.clientY });
      setTouchEnd(null);
    },
    [showMonthPicker, showYearPicker],
  );

  const handleTouchMove = React.useCallback(
    (e: Event) => {
      const touchEvent = e as TouchEvent;
      const target = touchEvent.target as HTMLElement;

      // Don't handle touch if inside year picker content (allow scrolling)
      if (
        showYearPicker &&
        (target.closest(".year-picker-content") ||
          target.closest(".year-picker-dropdown"))
      ) {
        return;
      }

      // Don't handle touch if month picker is open
      if (showMonthPicker) {
        return;
      }

      if (!touchStart) return;

      const touch = touchEvent.touches[0];
      const deltaX = touchStart.x - touch.clientX;
      const deltaY = touchStart.y - touch.clientY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // If horizontal movement is more significant, start tracking swipe
      if (absDeltaX > absDeltaY && absDeltaX > 10) {
        // Prevent default scrolling when horizontal swipe detected
        touchEvent.preventDefault();
      }

      setTouchEnd({ x: touch.clientX, y: touch.clientY });
    },
    [showMonthPicker, showYearPicker, touchStart],
  );

  const handleTouchEnd = React.useCallback(
    (e: Event) => {
      const touchEvent = e as TouchEvent;
      const target = touchEvent.target as HTMLElement;

      // Don't handle touch if inside year picker content (allow scrolling)
      if (
        showYearPicker &&
        (target.closest(".year-picker-content") ||
          target.closest(".year-picker-dropdown"))
      ) {
        return;
      }

      // Don't handle touch if month picker is open
      if (showMonthPicker) {
        return;
      }

      if (!touchStart || !touchEnd) {
        setTouchStart(null);
        setTouchEnd(null);
        return;
      }

      const deltaX = touchStart.x - touchEnd.x;
      const deltaY = touchStart.y - touchEnd.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // Only trigger if horizontal swipe is more significant than vertical
      // and the swipe distance is at least 50px
      if (absDeltaX > absDeltaY && absDeltaX > 30) {
        touchEvent.preventDefault();

        if (deltaX > 0) {
          // Swipe left - next month
          if (!nextMonthButtonDisabled) {
            increaseMonth();
          }
        } else if (!prevMonthButtonDisabled) {
          // Swipe right - previous month
          decreaseMonth();
        }
      }

      // Reset touch states
      setTouchStart(null);
      setTouchEnd(null);
    },
    [
      touchStart,
      touchEnd,
      increaseMonth,
      decreaseMonth,
      nextMonthButtonDisabled,
      prevMonthButtonDisabled,
      showMonthPicker,
      showYearPicker,
    ],
  );

  // Add wheel event listener to calendar
  React.useEffect(() => {
    const calendarElement = document.querySelector(
      ".custom-datepicker-calendar",
    );
    if (calendarElement) {
      calendarElement.addEventListener("wheel", handleWheel, {
        passive: false,
      });
      return () => {
        calendarElement.removeEventListener("wheel", handleWheel);
      };
    }
  }, [handleWheel]);
  const isMobile = useIsMobile();
  // Add touch event listeners for mobile swipe navigation
  React.useEffect(() => {
    const calendarElement = document.querySelector(
      ".custom-datepicker-calendar",
    );
    if (calendarElement && isMobile) {
      calendarElement.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      calendarElement.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      calendarElement.addEventListener("touchend", handleTouchEnd, {
        passive: false,
      });

      return () => {
        calendarElement.removeEventListener("touchstart", handleTouchStart);
        calendarElement.removeEventListener("touchmove", handleTouchMove);
        calendarElement.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, isMobile]);

  // Handle click outside to close pickers
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".custom-datepicker-calendar")) {
        setShowMonthPicker(false);
        setShowYearPicker(false);
      }
    };

    if (showMonthPicker || showYearPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showMonthPicker, showYearPicker]);

  React.useEffect(() => {
    if (showYearPicker) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Find the selected year button by matching the text content with current year
        const currentYear = date.getFullYear().toString();
        const yearButtons = document.querySelectorAll(
          ".year-picker-content button",
        );
        const selectedYearButton = Array.from(yearButtons).find(
          (button) => button.textContent?.trim() === currentYear,
        );

        if (selectedYearButton) {
          selectedYearButton.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
  }, [showYearPicker, date]);
  const getMonthButtonClass = (
    monthValue: number,
    isDisabled: undefined | boolean,
  ) => {
    if (date.getMonth() === monthValue) {
      return "bg-[var(--fill-base-main)] text-white hover:bg-[var(--fill-primary-solid-strongest)]";
    }
    if (isDisabled) {
      return "cursor-not-allowed text-gray-400 opacity-50";
    }
    return "hover:bg-[var(--fill-primary-tonal-lighter)]";
  };

  const getYearButtonClass = (
    year: number,
    isDisabled: undefined | boolean,
  ) => {
    if (date.getFullYear() === year) {
      return "bg-[var(--fill-base-main)] text-white hover:bg-[var(--fill-primary-solid-strongest)]";
    }
    if (isDisabled) {
      return "cursor-not-allowed text-gray-400 opacity-50";
    }
    return "hover:bg-[var(--fill-primary-tonal-lighter)]";
  };

  return (
    <div className="bg-white">
      {/* Main Header - Hide when picker is open */}
      {!showMonthPicker && !showYearPicker && (
        <div className="flex w-full items-center justify-center border-b border-gray-200 pb-0.5">
          <button
            type="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            className="cursor-pointer rounded p-1 hover:bg-[var(--fill-primary-tonal-lighter)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
          </button>

          <div className="grid w-[86%] grid-cols-2 items-center">
            <button
              onClick={() => {
                setShowMonthPicker(!showMonthPicker);
                setShowYearPicker(false);
              }}
              className="rounded px-2 py-1 text-sm font-medium hover:bg-[var(--fill-primary-tonal-lighter)]"
            >
              {months[date.getMonth()].fullLabel}
            </button>

            <button
              onClick={() => {
                setShowYearPicker(!showYearPicker);
                setShowMonthPicker(false);
              }}
              className="rounded px-2 py-1 text-sm font-medium hover:bg-[var(--fill-primary-tonal-lighter)]"
            >
              {date.getFullYear()}
            </button>
          </div>

          <button
            type="button"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            className="cursor-pointer rounded p-1 hover:bg-[var(--fill-primary-tonal-lighter)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
          </button>
        </div>
      )}
      {/* Month Picker */}
      {showMonthPicker && (
        <div className="flex w-[250px] flex-col p-2 md:w-[270px]">
          <div className="hide-scrollbar max-h-[200px] flex-1 overflow-y-auto">
            <div className="grid grid-cols-3 gap-1">
              {months.map((month) => {
                // Check if this month should be disabled
                const isDisabled =
                  minDate &&
                  new Date(date.getFullYear(), month.value, 1) <
                    new Date(minDate.getFullYear(), minDate.getMonth(), 1);

                return (
                  <button
                    key={month.value}
                    onClick={() => handleMonthSelect(month.value)}
                    disabled={isDisabled}
                    className={`min-h-[2rem] rounded p-2 text-sm font-medium transition-all duration-150 sm:p-1.5 sm:text-xs md:min-h-[auto] md:p-2 md:text-sm ${getMonthButtonClass(month.value, isDisabled)}`}
                  >
                    {month.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Calendar button to exit month picker */}
          <div className="mt-2 flex-shrink-0 border-t border-gray-200 pt-2">
            <button
              type="button"
              onClick={() => setShowMonthPicker(false)}
              className="mb-1 flex w-full items-center justify-center space-x-2 rounded p-2 text-sm font-medium text-gray-600 transition-all duration-150 hover:scale-105 hover:bg-[var(--fill-primary-tonal-lighter)]"
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Year Picker */}
      {showYearPicker && (
        <div className="year-picker-dropdown flex max-h-[270px] w-[270px] flex-col p-2 sm:w-[250px] md:w-[270px]">
          <div className="hide-scrollbar year-picker-content h-[240px] flex-1 overflow-y-auto">
            <div className="grid grid-cols-3 gap-1">
              {years.map((year) => {
                // Check if this year should be disabled
                const isDisabled = minDate && year < minDate.getFullYear();

                return (
                  <button
                    type="button"
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    disabled={isDisabled}
                    className={`min-h-[2rem] rounded p-2 text-sm font-medium transition-all duration-150 sm:p-1.5 sm:text-xs md:min-h-[auto] md:p-2 md:text-sm ${getYearButtonClass(year, isDisabled)}`}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Calendar button to exit year picker - Always visible at bottom */}
          <div className="mt-2 flex-shrink-0 border-t border-gray-200 pt-2">
            <button
              type="button"
              onClick={() => setShowYearPicker(false)}
              className="mb-1 flex w-full items-center justify-center space-x-2 rounded p-2 text-sm font-medium text-gray-600 transition-all duration-150 hover:scale-105 hover:bg-[var(--fill-primary-tonal-lighter)]"
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CustomDatePicker Component
 *
 * A fully customized date picker built on top of `react-datepicker`.
 * Includes mobile swipe navigation, wheel-based month switching,
 * and custom header UI for selecting months and years with constraints.
 */
export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder = "日付を選択してください",
  disabled = false,
  minDate,
  maxDate,
  showTimeSelect = false,
  timeFormat = "HH:mm",
  className,
  error,
  inline = false,
  showYearDropdown = true,
  showMonthDropdown = true,
  excludeDates,
  includeDates,
  filterDate,
  onFocus,
  onBlur,
  leftIcon,
  showRightIcon = true,
  onCalendarClose,
  onCalendarOpen,
}) => {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const datePickerRef = React.useRef<DatePicker>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const effectiveMinDate = minDate || today;

  // Handle calendar open/close state
  const handleCalendarOpen = React.useCallback(() => {
    setIsOpen(true);
    onCalendarOpen?.();
  }, [onCalendarOpen]);

  const handleCalendarClose = React.useCallback(() => {
    setIsOpen(false);
    onCalendarClose?.();
  }, [onCalendarClose]);

  // Close date picker when clicking on react-select elements
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOnSelect = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if click is on a react-select control or menu
      const isSelectControl = target.closest('.custom-select__control');
      const isSelectMenu = target.closest('.custom-select__menu');
      const isSelectElement = isSelectControl || isSelectMenu;
      
      // Check if click is outside the date picker
      const isOutsideDatePicker = !target.closest('.custom-datepicker') && 
                                   !target.closest('.react-datepicker-popper') &&
                                   !target.closest('.custom-datepicker-popper');
      
      // Close date picker if clicking on select element
      if (isSelectElement && isOutsideDatePicker) {
        datePickerRef.current?.setOpen(false);
      }
    };

    // Listen to both mouse and touch events
    document.addEventListener('mousedown', handleClickOnSelect);
    document.addEventListener('touchstart', handleClickOnSelect, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOnSelect);
      document.removeEventListener('touchstart', handleClickOnSelect);
    };
  }, [isOpen]);

  return (
    <div className="custom-datepicker relative">
      {leftIcon && (
        <div className="pointer-events-none absolute left-3 top-1/2  -translate-y-1/2 transform">
          {leftIcon}
        </div>
      )}
      <DatePicker
        ref={datePickerRef}
        selected={value}
        onChange={onChange}
        minDate={effectiveMinDate}
        maxDate={maxDate}
        dateFormat={
          showTimeSelect
            ? `yyyy年MM月dd日 (eee) ${timeFormat}`
            : "yyyy年MM月dd日 (eee)"
        }
        locale={"ja"}
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        disabled={disabled}
        placeholderText={placeholder}
        className={cn(`w-full rounded-lg border py-2.5 text-sm focus:border-[var(--stroke-base-primary)] focus:ring-1 focus:outline-none ${leftIcon ? "pl-10" : "px-3"} ${showRightIcon ? "pr-10" : "pr-3"} ${error ? "border-red-500" : "border-[var(--stroke-gray-normal)]"}`, className)}
        excludeDates={excludeDates}
        includeDates={includeDates}
        filterDate={filterDate}
        showYearDropdown={showYearDropdown}
        showMonthDropdown={showMonthDropdown}
        inline={inline}
        onFocus={onFocus}
        onBlur={onBlur}
        onCalendarOpen={handleCalendarOpen}
        onCalendarClose={handleCalendarClose}
        disabledKeyboardNavigation
        renderCustomHeader={(props) => (
          <CustomHeader
            {...props}
            onPickerStateChange={setIsPickerOpen}
            minDate={effectiveMinDate}
          />
        )}
        popperClassName="custom-datepicker-popper"
        calendarClassName={`custom-datepicker-calendar ${isPickerOpen ? "hide-days" : ""}`}
      />
      {showRightIcon && (
        <CalendarDays className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default CustomDatePicker;
